import { useState } from 'react'
import { api } from '../../api/endpoints'
import { errorMessage, isManagedConflict } from '../../api/client'
import { syncAll } from '../../sync/syncEngine'
import { useConversation } from '../conversation/ConversationContext'
import { toastChannelError } from './channelErrors'

export interface ChannelNameAndTopic {
  name: string
  topic: string
}

interface Props {
  onDone: () => void
  /**
   * Sysadmin editing a managed channel: the values go to the caller, which asks for confirmation and saves
   * with ?override=true, instead of being saved here.
   */
  onSubmitOverride?: (values: ChannelNameAndTopic) => void
}

export function EditChannelForm({ onDone, onSubmitOverride }: Props) {
  const { channel } = useConversation()
  const [name, setName] = useState(channel.name ?? '')
  const [topic, setTopic] = useState(channel.topic ?? '')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  return (
    <form
      style={{ textAlign: 'left', marginTop: 8 }}
      onSubmit={async (e) => {
        e.preventDefault()
        if (!name.trim()) return setError('The channel needs a name.')
        const values = { name: name.trim(), topic: topic.trim() }
        if (onSubmitOverride) {
          setError(null)
          onSubmitOverride(values)
          return
        }
        setBusy(true)
        try {
          await api.patchChannel(channel.id, values)
          await syncAll()
          onDone()
        } catch (err) {
          if (isManagedConflict(err)) {
            toastChannelError('', err)
            onDone()
          } else {
            setError(errorMessage(err))
          }
        } finally {
          setBusy(false)
        }
      }}
    >
      <div className="field">
        <label className="field-label" htmlFor="edit-channel-name">
          Name
        </label>
        <input id="edit-channel-name" className="text-input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} autoFocus />
      </div>
      <div className="field">
        <label className="field-label" htmlFor="edit-channel-topic">
          Topic
        </label>
        <textarea id="edit-channel-topic" className="text-input" value={topic} maxLength={250} onChange={(e) => setTopic(e.target.value)} />
      </div>
      {error ? (
        <p className="field-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="row-actions" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="button small" onClick={onDone}>
          Cancel
        </button>
        <button type="submit" className="button primary small" disabled={busy}>
          Save
        </button>
      </div>
    </form>
  )
}
